/**
 * Throttle: triggers at most once per `wait` ms (leading + trailing).
 * @param {Function} fn
 * @param {number} [wait=300]
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => { tick = false; }, wait);
    }
  };
}

if (!customElements.get('scrolling-image-list')) {
  class ScrollingImageList extends HTMLElement {
    constructor() {
      super();
      this._initialized = false;
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      // Lazy-init via IntersectionObserver — 500px rootMargin matches
      // Symmetry's initLazyScript default threshold.
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.init();
                observer.unobserve(entry.target);
              }
            });
          },
          { rootMargin: '0px 0px 500px 0px' }
        );
        io.observe(this);
      } else {
        this.init();
      }
    }

    init() {
      // Forward section padding vars from data attributes
      const pt = this.dataset.paddingTop;
      const pb = this.dataset.paddingBottom;
      if (pt) this.style.setProperty('--section-padding-top', `${pt}px`);
      if (pb) this.style.setProperty('--section-padding-bottom', `${pb}px`);

      this.images = this.querySelectorAll('.scrolling-image-list__image');
      this.slider  = this.querySelector('.slider');

      if (this.images.length > 1) {
        this.contentColumn = this.querySelector('.scrolling-image-list__content-column');
        this.featureText   = this.querySelector('.scrolling-image-list__content-column .feature-text-paired');

        const mq = window.matchMedia('(min-width: 750px)');
        this.addListeners(mq);
        mq.addEventListener('change', (event) => {
          this.removeListeners();
          this.addListeners(event);
        });
      }

      this.setAttribute('loaded', '');
    }

    /**
     * @param {MediaQueryList|MediaQueryListEvent} mq
     */
    addListeners(mq) {
      if (mq.matches) {
        // ── DESKTOP: scroll-driven sticky text + click-to-scroll images ──
        this.throttledHandleScroll = throttle(this.handleScroll.bind(this), 100);
        window.addEventListener('scroll', this.throttledHandleScroll);

        this.boundUpdatePositioning = this.updatePositioning.bind(this);
        window.addEventListener('scroll', this.boundUpdatePositioning);
        window.addEventListener('resize', this.boundUpdatePositioning);

        // Run immediately after binding
        this.throttledHandleScroll();
        this.boundUpdatePositioning();

        // Clicking an image scrolls it to viewport centre
        this.contentClickListeners = [];
        this.querySelectorAll('.scrolling-image-list__image').forEach((el) => {
          const oc = () => this.scrollToImage(el.dataset.index);
          this.contentClickListeners.push({ el, oc });
          el.addEventListener('click', oc);
        });

        // Keyboard tabbing
        this.delegatedKeydownHandler = this._addDelegateEventListener(
          this,
          'keydown',
          '.scrolling-image-list__image, .scrolling-image-list__content:not(.scrolling-image-list__content--mobile), .scrolling-image-list__content:not(.scrolling-image-list__content--mobile) a',
          this.handleKeydown.bind(this)
        );
      } else {
        // ── MOBILE: horizontal swipe carousel ──
        // Listen to the slider's scroll event to track which slide is active.
        // This replaces Symmetry's carousel-slider custom element + on:carousel-slider:select event.
        this.throttledHandleCarouselScroll = throttle(this.handleCarouselScroll.bind(this), 80);
        if (this.slider) {
          this.slider.addEventListener('scroll', this.throttledHandleCarouselScroll, { passive: true });
        }

        // Run once immediately to set initial state
        this.handleCarouselScroll();
      }
    }

    removeListeners() {
      // Desktop
      window.removeEventListener('scroll', this.throttledHandleScroll);
      window.removeEventListener('scroll', this.boundUpdatePositioning);
      window.removeEventListener('resize', this.boundUpdatePositioning);

      if (this.contentClickListeners) {
        this.contentClickListeners.forEach(({ el, oc }) => el.removeEventListener('click', oc));
        this.contentClickListeners = null;
      }

      if (this.delegatedKeydownHandler) {
        this.removeEventListener('keydown', this.delegatedKeydownHandler);
        this.delegatedKeydownHandler = null;
      }

      // Mobile
      if (this.throttledHandleCarouselScroll && this.slider) {
        this.slider.removeEventListener('scroll', this.throttledHandleCarouselScroll);
        this.throttledHandleCarouselScroll = null;
      }
    }

    /**
     * Mobile carousel: called on slider scroll.
     * Determines which slide is most in view based on scroll position,
     * then updates data-current-index (drives dot + bg colour via CSS).
     */
    handleCarouselScroll() {
      if (!this.slider) return;

      const sliderWidth = this.slider.clientWidth;
      if (sliderWidth === 0) return;

      // Round to nearest slide based on scrollLeft
      const index = Math.round(this.slider.scrollLeft / sliderWidth) + 1;
      const clamped = Math.max(1, Math.min(index, this.images.length));

      if (parseInt(this.dataset.currentIndex, 10) !== clamped) {
        this.dataset.currentIndex = clamped;
      }
    }

    /**
     * Desktop: throttled window scroll — finds image closest to viewport
     * middle and updates data-current-index.
     */
    handleScroll() {
      let closestIndex    = 1;
      let closestDistance = Number.MAX_VALUE;

      this.images.forEach((el, index) => {
        const distance = ScrollingImageList.distanceFromViewportMiddle(el);
        if (distance < closestDistance) {
          closestIndex    = index + 1;
          closestDistance = distance;
        }
      });

      this.dataset.currentIndex = closestIndex;
    }

    /**
     * Keyboard tabbing handler — keeps tab order logical.
     * @param {Event} evt
     * @param {Element} el - matched delegated target
     */
    handleKeydown(evt, el) {
      if (evt.code !== 'Tab') return;

      if (el.classList.contains('scrolling-image-list__image')) {
        if (!evt.shiftKey) {
          evt.preventDefault();
          this.querySelector(`.scrolling-image-list__content:not(.scrolling-image-list__content--mobile)[data-index="${el.dataset.index}"]`).focus();
          return;
        }
        if (el.dataset.index !== '1') {
          evt.preventDefault();
          this.querySelector(`.scrolling-image-list__image[data-index="${parseInt(el.dataset.index, 10) - 1}"]`).focus();
        }
        return;
      }

      if (el.classList.contains('scrolling-image-list__content')) {
        if (evt.shiftKey) {
          evt.preventDefault();
          this.querySelector(`.scrolling-image-list__image[data-index="${el.dataset.index}"]`).focus();
          return;
        }
        if (el.nextElementSibling !== null) {
          evt.preventDefault();
          const link = el.querySelector('a');
          if (link) {
            link.focus();
          } else {
            this.querySelector(`.scrolling-image-list__image[data-index="${parseInt(el.dataset.index, 10) + 1}"]`).focus();
          }
        }
        return;
      }

      if (el.tagName === 'A') {
        const contentContainer = el.closest('.scrolling-image-list__content');
        if (contentContainer && contentContainer.nextElementSibling !== null) {
          const lastA = [...contentContainer.querySelectorAll('a')].pop();
          if (lastA === el && !evt.shiftKey) {
            evt.preventDefault();
            this.querySelector(`.scrolling-image-list__image[data-index="${parseInt(contentContainer.dataset.index, 10) + 1}"]`).focus();
          }
        }
      }
    }

    /**
     * Desktop: sets CSS custom properties positioning the sticky text column.
     * Called on every scroll + resize.
     */
    updatePositioning() {
      const containerBcr   = this.contentColumn.getBoundingClientRect();
      const containerOffset = containerBcr.top + window.scrollY;
      const contentHeight  = this.featureText.getBoundingClientRect().height;

      let bcr           = this.images[0].getBoundingClientRect();
      const topImageMid = bcr.top + window.scrollY + bcr.height / 2;

      bcr               = this.images[this.images.length - 1].getBoundingClientRect();
      const botImageMid = bcr.top + window.scrollY + bcr.height / 2;

      const windowMid = window.scrollY + window.innerHeight / 2;

      if (windowMid < topImageMid) {
        this.style.setProperty('--scrolling-image-list-content-offset-y', `${topImageMid - containerOffset - contentHeight / 2}px`);
        this.style.removeProperty('--scrolling-image-list-content-position');
        this.style.removeProperty('--scrolling-image-list-content-offset-x');
        this.style.removeProperty('--scrolling-image-list-content-width');
      } else if (windowMid > botImageMid) {
        this.style.setProperty('--scrolling-image-list-content-offset-y', `${botImageMid - containerOffset - contentHeight / 2}px`);
        this.style.removeProperty('--scrolling-image-list-content-position');
        this.style.removeProperty('--scrolling-image-list-content-offset-x');
        this.style.removeProperty('--scrolling-image-list-content-width');
      } else {
        this.style.setProperty('--scrolling-image-list-content-position', 'fixed');
        this.style.setProperty('--scrolling-image-list-content-offset-y', `${window.innerHeight / 2 - contentHeight / 2}px`);
        this.style.setProperty('--scrolling-image-list-content-offset-x', `${containerBcr.left}px`);
        this.style.setProperty('--scrolling-image-list-content-width', `${containerBcr.width}px`);
      }
    }

    /**
     * Desktop: smooth-scrolls so the given image sits in viewport centre.
     * @param {number|string} index - 1-based
     */
    scrollToImage(index) {
      const image = this.images[index - 1];
      window.scrollTo({
        left: 0,
        top: this._getOffsetTopFromDoc(image) - (window.innerHeight - image.clientHeight) / 2,
        behavior: 'smooth',
      });
    }

    // ── Helpers ─────────────────────────────────────────────────────────

    _addDelegateEventListener(parent, eventName, selector, handler) {
      const listener = (evt) => {
        const target = evt.target.closest(selector);
        if (target && parent.contains(target)) {
          handler(evt, target);
        }
      };
      parent.addEventListener(eventName, listener);
      return listener;
    }

    _getOffsetTopFromDoc(el) {
      return el.getBoundingClientRect().top + window.scrollY;
    }

    static distanceFromViewportMiddle(el) {
      const viewportMidY = window.scrollY + window.innerHeight / 2;
      const imageMidY    = el.getBoundingClientRect().top + window.scrollY + el.clientHeight / 2;
      return Math.abs(viewportMidY - imageMidY);
    }
  }

  customElements.define('scrolling-image-list', ScrollingImageList);
}